import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";
import { requestPasswordReset } from "@/services/auth";
import { PublicLegalFooter } from "@/components/PublicLegalFooter";

type State = "form" | "submitting" | "submitted";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("form");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setState("submitting");
    // Mirror backend enumeration shield: show same banner regardless of outcome.
    // Network/5xx errors are intentionally swallowed so a leaked error message
    // can't be used to probe whether the address is registered.
    try {
      await requestPasswordReset(email);
    } catch {
      // No-op: enumeration shield, see comment above.
    }
    setState("submitted");
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>QuizNudge</CardTitle>
          <CardDescription>Passwort zurücksetzen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "submitted" ? (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
              <h2 className="text-xl font-semibold">E-Mail verschickt</h2>
              <p className="text-muted-foreground">
                Wenn die E-Mail-Adresse bei uns registriert ist, schicken wir
                dir einen Link zum Zurücksetzen deines Passworts.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">Zurück zur Anmeldung</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Gib deine E-Mail-Adresse ein. Wir schicken dir einen Link, mit
                dem du dein Passwort neu setzen kannst.
              </p>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={state === "submitting"}
              >
                {state === "submitting"
                  ? "Wird gesendet …"
                  : "Link anfordern"}
              </Button>
              <p className="text-center text-sm">
                <Link to="/login" className="text-blue-600 hover:underline">
                  Zurück zur Anmeldung
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
      <PublicLegalFooter />
    </div>
  );
}
